import { Component, Inject, OnInit, ChangeDetectionStrategy } from '@angular/core'
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import {
  ConfirmDialogService,
  ConfirmDialogStatus,
  Destroyable,
  SaveForm,
  Toaster,
  selectAllRentals,
  untilDestroy,
  ChannelNameTokens,
  DataCheckerService,
  isSomething,
  openWindow,
  AlertDialogService,
} from '@tokeet-frontend/tv3-platform'
import { Store, select } from '@ngrx/store'
import { AiResponderService } from '../store/ai-responder.service'
import { InquiryService } from '@tv3/store/inquiry/inquiry.service'
import { filter, finalize, switchMap, tap, observeOn, map, takeWhile, delay } from 'rxjs/operators'
import { AuthService } from '@tv3/services/auth.service'
import { asyncScheduler, interval } from 'rxjs'
import { AIToneOptions, AiNotifyMeOption, AiResponderSettings } from '../store/ai-settings.model'
import { integer } from '@tokeet-frontend/tv3-platform'
import { selectBookingSourcesWithCustomChannels } from '@tv3/store/channel/selectors'
import * as lodash from 'lodash'
import { environment } from '@tv3/environments/environment'
import { ChannelGuard } from '@tv3/guards/channel.guard'
import { RentalGuard } from '@tv3/guards/rental.guard'
import { AISettingsGuard } from '@tv3/containers/ai-responder/store/ai-settings.guard'
import { selectAISettings, selectAISettingsIsSaving } from '../store/ai-settings.selectors'
import { LoadAISettings, UpdateAISettings, LoadAISettingsComplete } from '../store/ai-settings.actions'
import { CustomContractSigner, SignatureService } from '@tokeet-frontend/signature'
import { AmplitudeService } from '@tv3/services/amplitude.service'

export enum AiResponderSettingsTabs {
  General,
  Settings,
  RentalDetails,
  IgnoreList,
  IncidentTask,
  CreditRecharge,
  OffCalendar,
}

@Component({
  templateUrl: './ai-responder-settings.component.html',
  styleUrls: ['./ai-responder-settings.component.scss'],
  host: { class: 'template-overlay' },
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AiResponderSettingsComponent extends Destroyable implements OnInit {
  tabs = AiResponderSettingsTabs
  activeTab = AiResponderSettingsTabs.General
  minuteOptions = [5, 10, 15, 30, 60]
  channels$ = this.store.pipe(
    select(selectBookingSourcesWithCustomChannels({ inquirySource: 'tokeet' })),
    map((sources) =>
      lodash
        .filter(sources, (s: any) => s.id !== ChannelNameTokens.AirBnBICal)
        .map((s) =>
          s.id !== ChannelNameTokens.AirBnBV2API
            ? s
            : { ...s, id: ChannelNameTokens.AirBnBICal, name: ChannelNameTokens.AirBnBICal }
        )
    )
  )
  toneOptions = AIToneOptions
  notifyMeOptions: { value: AiNotifyMeOption; label: string }[] = [
    { value: 'email', label: $localize`Email` },
    { value: 'sms', label: $localize`SMS` },
  ]

  settingsForm = this.fb.group({
    channels: [[]],
    notifyMeEnabled: [false],
    notifyMe: [[] as AiNotifyMeOption[]],
    ignoreCourtesies: [false],
    delayResponse: [15, [Validators.min(1), integer()]],
    isAutoRespondToNewBookings: [false],
    isDelayResponse: [false],
    isRespondEvenIfResponded: [false],
    ignoreCancellations: [false],
    isCreateDrafts: [false],
    isTranslateEnabled: [false],
    isGenerateGuestSummary: [false],
    name: ['Advance Intelligence', [Validators.required]],
    tone: ['business', [Validators.required]],
    maxReplies: [1, [Validators.required, Validators.min(1), integer()]],
  })

  testForm = this.fb.group({
    rentalId: [undefined, [Validators.required]],
    inquiryId: [undefined, [Validators.required]],
    tone: ['business', [Validators.required]],
    message: ['', [Validators.required]],
    answer: [],
  })

  isGeneratingResponse = false
  isLoadingBookings = false
  rentals$ = this.store.pipe(observeOn(asyncScheduler), select(selectAllRentals))
  bookings$ = this.testForm.get('rentalId').valueChanges.pipe(
    filter((id) => !!id),
    tap(() => (this.isLoadingBookings = true)),
    switchMap((rentalId) => this.inquiryService.getBy({ rentals: [rentalId] })),
    tap(() => (this.isLoadingBookings = false))
  )

  togglesForm = this.fb.group({
    isEnabled: [],
    isAutomateSendReplies: [false],
    isWebsitesEnabled: [],
    isDocumentsEnabled: [],
    isFaqEnabled: [],
  })

  isSaving$ = this.store.pipe(select(selectAISettingsIsSaving))
  isTestFormChanged = false
  isTestPopoverOpen = false
  aiSettings: AiResponderSettings

  constructor(
    private fb: UntypedFormBuilder,
    private store: Store<any>,
    private toaster: Toaster,
    private confirm: ConfirmDialogService,
    private alert: AlertDialogService,
    private dataChecker: DataCheckerService,
    public dialogRef: MatDialogRef<AiResponderSettingsComponent>,
    private aiResponderService: AiResponderService,
    private inquiryService: InquiryService,
    private auth: AuthService,
    private signature: SignatureService,
    @Inject(MAT_DIALOG_DATA) private data: { tab?: AiResponderSettingsTabs },
    private amplitudeService: AmplitudeService
  ) {
    super()
    this.dataChecker.check([ChannelGuard, RentalGuard, AISettingsGuard])
    this.activeTab = this.data.tab || AiResponderSettingsTabs.General
  }

  ngOnInit(): void {
    this.amplitudeService.logEvent('ai-responder-settings-view')
    this.store.pipe(select(selectAISettings), filter(isSomething), untilDestroy(this)).subscribe((settings) => {
      this.aiSettings = lodash.cloneDeep(settings)
      this.togglesForm.patchValue(settings, { emitEvent: false })
      this.settingsForm.patchValue(
        {
          ...settings,
          notifyMe: this.getNotifyMeValue(settings),
          notifyMeEnabled: this.isNotifyMeEnabled(settings),
        },
        { emitEvent: false }
      )
      if (!settings.isEnabled) {
        this.togglesForm.disable({ emitEvent: false })
        this.settingsForm.disable({ emitEvent: false })
        this.togglesForm.get('isEnabled').enable({ emitEvent: false })
      } else {
        this.togglesForm.enable({ emitEvent: false })
        this.settingsForm.enable({ emitEvent: false })
      }

      this.testForm.get('tone').setValue(settings.tone || 'business', { emitEvent: false })
    })

    this.testForm.valueChanges.pipe(untilDestroy(this)).subscribe(() => {
      this.isTestFormChanged = true
    })

    const watchToggle = (form: UntypedFormGroup, key: string, cb: (checked: boolean) => void) => {
      form.get(key).valueChanges.pipe(untilDestroy(this)).subscribe(cb)
    }
    watchToggle(this.togglesForm, 'isEnabled', (checked) => this.onToggleAI(checked))
    watchToggle(this.togglesForm, 'isAutomateSendReplies', (checked) => this.onToggleAutoReply(checked))
    watchToggle(this.togglesForm, 'isWebsitesEnabled', (checked) => this.onToggleWebsites(checked))
    watchToggle(this.togglesForm, 'isDocumentsEnabled', (checked) => this.onToggleDocuments(checked))
    watchToggle(this.togglesForm, 'isFaqEnabled', (checked) => this.onToggleFAQs(checked))
    // settings
    watchToggle(this.settingsForm, 'isAutoRespondToNewBookings', (checked) =>
      this.onToggleSettings('isAutoRespondToNewBookings', checked)
    )
    watchToggle(this.settingsForm, 'isDelayResponse', (checked) => this.onToggleSettings('isDelayResponse', checked))
    watchToggle(this.settingsForm, 'isRespondEvenIfResponded', (checked) =>
      this.onToggleSettings('isRespondEvenIfResponded', checked)
    )
    watchToggle(this.settingsForm, 'ignoreCancellations', (checked) =>
      this.onToggleSettings('ignoreCancellations', checked)
    )
    watchToggle(this.settingsForm, 'isTranslateEnabled', (checked) =>
      this.onToggleSettings('isTranslateEnabled', checked)
    )
    watchToggle(this.settingsForm, 'isGenerateGuestSummary', (checked) =>
      this.onToggleSettings('isGenerateGuestSummary', checked)
    )
    watchToggle(this.settingsForm, 'ignoreCourtesies', (checked) => this.onToggleSettings('ignoreCourtesies', checked))
    watchToggle(this.settingsForm, 'notifyMeEnabled', (checked) => this.onToggleNotifyMe(checked))

    watchToggle(this.settingsForm, 'isCreateDrafts', (checked) => {
      if (checked) {
        this.confirm
          .openRegular({
            title: $localize`Warning!`,
            body: $localize`Your Advance Intelligence balance will be used for each generated draft. Do you want to enable it?`,
          })
          .subscribe((status) => {
            if (status === ConfirmDialogStatus.Confirmed) {
              this.onToggleSettings('isCreateDrafts', checked)
            } else {
              this.settingsForm.get('isCreateDrafts').setValue(false, { emitEvent: false })
            }
          })
      } else {
        this.onToggleSettings('isCreateDrafts', checked)
      }
    })
  }

  close() {
    this.amplitudeService.logEvent('ai-responder-settings-close')
    this.dialogRef.close(this.aiSettings.isEnabled)
  }

  onRefreshSettings() {
    this.store.dispatch(LoadAISettings())
  }

  onToggleAI(checked: boolean) {
    this.amplitudeService.logEvent('ai-responder-toggle')
    if (checked) {
      this.aiResponderService
        .getSettings()
        .pipe(
          tap((settings) => this.store.dispatch(LoadAISettingsComplete({ settings }))),
          delay(100)
        )
        .subscribe(() => {
          this.onEnableAI()
        })
    } else {
      this.onDisableAI()
    }
  }

  onDisableAI() {
    this.confirm
      .confirm({
        title: $localize`Disable Advance Intelligence?`,
        body: $localize`Are you sure you want to disable Advance Intelligence?`,
        confirmText: $localize`Disable`,
      })
      .subscribe(() => {
        this.store.dispatch(
          UpdateAISettings({
            payload: {
              isEnabled: false,
              isAutomateSendReplies: false,
              // isWebsitesEnabled: false,
              isDocumentsEnabled: false,
              isFaqEnabled: false,
            },
            message: $localize`Advance Intelligence feature has been disabled successfully`,
          })
        )
      })
  }
  onEnableAI() {
    if (this.aiSettings?.isContractSigned && this.aiSettings?.contractId) {
      this.store.dispatch(
        UpdateAISettings({
          payload: {
            isEnabled: true,
          },
          message: $localize`Advance Intelligence feature has been enabled successfully`,
        })
      )

      return
    }

    if (this.aiSettings?.contractId) {
      this.alert
        .openResult({
          title: $localize`Advance Intelligence Agreement Not Signed`,
          body: $localize`Please sign Advance Intelligence agreement to enable Advance Intelligence feature`,
        })
        .pipe(switchMap(() => this.openContract(this.aiSettings.contractId)))
        .subscribe()
      return
    }

    const terms = 'https://advance.cm/advance-intelligence-ai-terms-conditions/'
    this.confirm
      .confirmExtra(
        {
          title: $localize`Enable Advance Intelligence?`,
          body: $localize`Welcome to Advance Intelligence! Please take a moment to review & sign Advance Intelligence agreement to proceed & Accept our Terms and Conditions to ensure a smooth and secure experience.`,
          extra:
            $localize`I agree to the ` + `<a href="${terms}" target="_blank">` + $localize`Terms & Conditions` + `</a>`,
          extraCheckedRequired: true,
          extraCheckedRequiredMsg: $localize`Please select checkbox to proceed`,
          confirmText: $localize`Sign`,
          confirmBtnTooltip: $localize`Sign Advance Intelligence agreement to enable Advance Intelligence feature`,
        },
        true
      )
      .pipe(
        filter((res) => {
          if (res.status !== ConfirmDialogStatus.Confirmed || !res.isChecked) {
            this.togglesForm.get('isEnabled').setValue(false, { emitEvent: false })
            return false
          }
          return true
        }),
        switchMap(() => {
          const signer: CustomContractSigner = {
            email: this.auth.user.primaryEmail,
            name: this.auth.user.name,
            role: environment.config.tokeetAIContractSignerRole,
          }
          return this.signature.sendContractViaEmail(environment.config.tokeetAIContractId, [signer])
        }),
        switchMap((contract) => {
          this.store.dispatch(
            UpdateAISettings({
              payload: { contractId: contract.contract_id, isContractSigned: false },
              silent: true,
            })
          )
          // keep it disabled before sign the contract
          this.togglesForm.get('isEnabled').setValue(false, { emitEvent: false })
          return this.openContract(contract.contract_id)
        })
      )
      .subscribe()
  }

  onToggleAutoReply(checked: boolean) {
    this.amplitudeService.logEvent('ai-responder-auto-reply-toggle')
    const update = () => {
      this.store.dispatch(
        UpdateAISettings({
          payload: { isAutomateSendReplies: checked },
          message:
            $localize`Automatically sending Advance Intelligence message option is ` +
            (checked ? $localize`enabled` : $localize`disabled`),
        })
      )
    }
    if (!checked) {
      update()
      return
    }
    this.confirm
      .confirm({
        title: $localize`Enable Automatically Send AI Replies?`,
        body: $localize`Do you want to automatically send AI based messages to guest as reply?`,
      })
      .subscribe(() => {
        update()
      })
  }

  onSignAIContract() {
    const contractId = this.aiSettings.contractId
    if (!contractId) {
      return
    }
    this.openContract(contractId).subscribe()
  }

  openContract(contractId: string) {
    return this.signature.getContractSignLink(contractId, environment.config.tokeetAIContractSignerRole).pipe(
      map(({ link }) => openWindow(link, null, 1200, 800)),
      switchMap((win: Window) => interval(2000).pipe(takeWhile(() => !win.closed))),
      untilDestroy(this),
      finalize(() => this.checkContractSigned())
    )
  }

  checkContractSigned() {
    interval(2000)
      .pipe(
        takeWhile(() => !this.aiSettings?.isContractSigned),
        untilDestroy(this)
      )
      .subscribe(() => {
        this.onRefreshSettings()
      })
  }

  onToggleWebsites(checked: boolean) {
    this.amplitudeService.logEvent('ai-responder-websites-toggle')
    this.store.dispatch(
      UpdateAISettings({
        payload: { isWebsitesEnabled: checked },
        message: $localize`Websites are now ` + (checked ? $localize`enabled` : $localize`disabled`),
      })
    )
  }

  onToggleDocuments(checked: boolean) {
    this.amplitudeService.logEvent('ai-responder-documents-toggle')
    this.store.dispatch(
      UpdateAISettings({
        payload: { isDocumentsEnabled: checked },
        message: $localize`Documents are now ` + (checked ? $localize`enabled` : $localize`disabled`),
      })
    )
  }

  onToggleFAQs(checked: boolean) {
    this.amplitudeService.logEvent('ai-responder-faqs-toggle')
    this.store.dispatch(
      UpdateAISettings({
        payload: { isFaqEnabled: checked },
        message: $localize`FAQs are now ` + (checked ? $localize`enabled` : $localize`disabled`),
      })
    )
  }

  onToggleSettings(key: string, checked: boolean) {
    this.store.dispatch(
      UpdateAISettings({
        payload: { [key]: checked },
        message: $localize`This feature is now ` + (checked ? $localize`enabled` : $localize`disabled`),
      })
    )
  }

  onToggleNotifyMe(checked: boolean) {
    if (checked) {
      return
    }

    this.settingsForm.get('notifyMe').setValue([], { emitEvent: false })
    this.store.dispatch(
      UpdateAISettings({
        payload: { notifyMe: [] },
        message: $localize`Notify Me by AI is now disabled`,
      })
    )
  }

  onToggleNotifyMeOption(option: AiNotifyMeOption, checked: boolean) {
    const notifyMe = checked
      ? lodash.uniq([...this.getNotifyMeSelections(), option])
      : this.getNotifyMeSelections().filter((value) => value !== option)

    this.settingsForm.get('notifyMe').setValue(notifyMe)

    if (!notifyMe.length && this.settingsForm.get('notifyMeEnabled').value) {
      this.settingsForm.get('notifyMeEnabled').setValue(false)
      return
    }

    if (notifyMe.length && !this.settingsForm.get('notifyMeEnabled').value) {
      this.settingsForm.get('notifyMeEnabled').setValue(true)
      return
    }

    this.store.dispatch(
      UpdateAISettings({
        payload: {
          notifyMe,
        },
        message: $localize`Notify Me by AI options have been updated`,
      })
    )
  }

  isNotifyMeOptionSelected(option: AiNotifyMeOption) {
    return this.getNotifyMeSelections().includes(option)
  }

  @SaveForm()
  onSaveSettings(form: UntypedFormGroup) {
    this.amplitudeService.logEvent('ai-responder-settings-save')
    const { notifyMeEnabled, notifyMe, ...payload } = this.settingsForm.getRawValue()
    this.store.dispatch(
      UpdateAISettings({
        payload: {
          ...payload,
          notifyMe: notifyMeEnabled ? notifyMe : [],
        },
      })
    )
  }

  private getNotifyMeSelections(): AiNotifyMeOption[] {
    return this.settingsForm.get('notifyMe').value || []
  }

  private getNotifyMeValue(settings: AiResponderSettings): AiNotifyMeOption[] {
    return settings.notifyMe || []
  }

  private isNotifyMeEnabled(settings: AiResponderSettings) {
    return this.getNotifyMeValue(settings).length > 0
  }

  @SaveForm()
  onTest(form: UntypedFormGroup) {
    this.amplitudeService.logEvent('ai-responder-test')
    const { answer, rentalId, ...payload } = this.testForm.getRawValue()
    this.isGeneratingResponse = true
    this.aiResponderService
      .generateReply({
        account: this.auth.accountId,
        convert: 'html',
        ...payload,
      })
      .pipe(finalize(() => (this.isGeneratingResponse = false)))
      .subscribe(
        (res) => {
          this.testForm.patchValue({ answer: res.message })
          this.isTestFormChanged = false
        },
        (error) => {
          this.toaster.error($localize`Unable to generate response`, '', error)
        }
      )
  }
}
